import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Skeleton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  alpha,
} from "@mui/material";
import {
  People,
  Engineering,
  Business,
  Leaderboard,
  AttachMoney,
  AccountBalance,
  TrendingUp,
  CalendarMonth,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Build,
  PersonOff,
  PersonPin,
  Receipt,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import DashboardService from "../../api/DashboardService";
import theme from "../../theme/theme";

const PRIMARY = "#0d8ca5";
const COLORS = ["#0d8ca5", "#00bfa5", "#7c4dff", "#ff5252", "#ff9800", "#4caf50", "#2196f3", "#e91e63"];
const STATUS_COLORS = {
  pending: "#ff9800",
  approved: "#4caf50",
  rejected: "#ff5252",
  paid: "#4caf50",
  cancelled: "#9e9e9e",
  under_construction: "#2196f3",
  handed_over: "#4caf50",
  under_service_plan: "#7c4dff",
  completed: "#4caf50",
  in_progress: "#2196f3",
};

const fmt = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
};

// ── Stat Card ──
const StatCard = ({ icon, title, value, color, sub }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "center",
      gap: 2,
      overflow: "hidden",
      minWidth: 0,
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
    }}
  >
    <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 52, height: 52, flexShrink: 0 }}>{icon}</Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700} noWrap>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {sub}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ── Chart Card wrapper ──
const ChartCard = ({ title, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
      minWidth: 0,
      ...sx,
    }}
  >
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="caption" fontWeight={600}>
        {label}
      </Typography>
      {payload.map((p, i) => (
        <Typography key={i} variant="body2" sx={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" && p.value > 999 ? fmt(p.value) : p.value}
        </Typography>
      ))}
    </Paper>
  );
};

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await DashboardService.fetchDashboard();
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2.5}>
          {[...Array(8)].map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: "16px" }} />
            </Grid>
          ))}
          {[...Array(4)].map((_, i) => (
            <Grid size={{ xs: 12, md: 6 }} key={i + 8}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: "16px" }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!data) return null;

  // ── Prepare chart data ──
  const leadStatusData = Object.entries(data.leads_by_status || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    value: v,
  }));

  const projectStatusData = Object.entries(data.projects_by_status || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v,
  }));

  const invoiceStatusData = Object.entries(data.invoices_by_status || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    value: v,
  }));

  const serviceStatusData = Object.entries(data.services_by_status || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v,
  }));

  const monthlyLeadsData = (data.monthly_leads || []).map((m) => ({
    month: monthLabel(m.month),
    Leads: m.count,
  }));

  const monthlyRevenueData = (data.monthly_revenue || []).map((m) => ({
    month: monthLabel(m.month),
    Total: parseFloat(m.total),
    Received: parseFloat(m.received),
  }));

  const liftTypeData = Object.entries(data.leads_by_lift_type || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v,
  }));

  const employeeData = Object.entries(data.employees_by_designation || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v,
  }));

  const thisMonthTotal = Object.values(data.this_month_services || {}).reduce((a, b) => a + b, 0);
  const thisMonthCompleted = data.this_month_services?.completed || 0;

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back! Here's an overview of your business.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {/* ── Row 1: KPI Cards ── */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<People />} title="Total Customers" value={data.total_customers} color="#0d8ca5" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<Engineering />} title="Active Employees" value={data.total_employees} color="#7c4dff" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<Business />} title="Total Projects" value={data.total_projects} color="#00bfa5" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<Leaderboard />} title="Total Leads" value={data.total_leads} color="#ff9800" />
        </Grid>

        {/* ── Row 2: Revenue Cards ── */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<AttachMoney />}
            title="Total Invoice Amount"
            value={fmt(data.total_invoice_amount)}
            color="#2196f3"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<AccountBalance />}
            title="Total Received"
            value={fmt(data.total_received)}
            color="#4caf50"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<TrendingUp />}
            title="Total Due"
            value={fmt(data.total_due)}
            color="#ff5252"
            sub={data.total_invoice_amount > 0
              ? `${((data.total_due / data.total_invoice_amount) * 100).toFixed(1)}% outstanding`
              : ""}
          />
        </Grid>

        {/* ── Row 3: Attendance + Services This Month ── */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<PersonPin />} title="Present Today" value={data.present_today} color="#4caf50" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<PersonOff />} title="Absent Today" value={data.absent_today} color="#ff5252" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<Build />}
            title="Services This Month"
            value={thisMonthTotal}
            color="#7c4dff"
            sub={`${thisMonthCompleted} completed`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<Receipt />} title="Total Services" value={data.total_services} color="#0d8ca5" />
        </Grid>

        {/* ── Row 4: Charts ── */}
        {/* Monthly Revenue Area Chart */}
        <Grid size={{ xs: 12, md: 8 }}>
          <ChartCard title="Monthly Revenue (Last 6 Months)">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyRevenueData}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d8ca5" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0d8ca5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorReceived" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt(v)} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="Total" stroke="#0d8ca5" fill="url(#colorTotal)" strokeWidth={2} />
                <Area type="monotone" dataKey="Received" stroke="#4caf50" fill="url(#colorReceived)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Invoice Status Pie */}
        <Grid size={{ xs: 12, md: 4 }}>
          <ChartCard title="Invoice Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={invoiceStatusData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {invoiceStatusData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={STATUS_COLORS[entry.name.toLowerCase()] || COLORS[i % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Monthly Leads Bar */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Monthly Leads (Last 6 Months)">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyLeadsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Leads" fill={PRIMARY} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Leads by Status Pie */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ChartCard title="Leads by Status">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={leadStatusData}
                  cx="50%"
                  cy="42%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {leadStatusData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={STATUS_COLORS[entry.name.toLowerCase()] || COLORS[i % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Projects by Status Pie */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ChartCard title="Projects by Status">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={projectStatusData}
                  cx="50%"
                  cy="42%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {projectStatusData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={
                        STATUS_COLORS[entry.name.toLowerCase().replace(/ /g, "_")] ||
                        COLORS[i % COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* ── Row 5: Lift Type + Employee + Service Status ── */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ChartCard title="Leads by Lift Type">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={liftTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#7c4dff" radius={[0, 6, 6, 0]} name="Leads" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ChartCard title="Employees by Designation">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={employeeData}
                  cx="50%"
                  cy="42%"
                  innerRadius={40}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {employeeData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ChartCard title="Service Status (All Time)">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={serviceStatusData}
                  cx="50%"
                  cy="42%"
                  innerRadius={40}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {serviceStatusData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={
                        STATUS_COLORS[entry.name.toLowerCase().replace(/ /g, "_")] ||
                        COLORS[i % COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* ── Row 6: Tables ── */}
        {/* Upcoming Services */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Upcoming Services (Next 7 Days)">
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Sr. No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Assigned To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.upcoming_services || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No upcoming services
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.upcoming_services.map((s, idx) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{new Date(s.date).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell>{s.customer}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.employee}
                            size="small"
                            variant="outlined"
                            color={s.employee === "Unassigned" ? "error" : "primary"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>

        {/* Recent Invoices */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Recent Invoices">
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Sr. No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Customer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Due</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recent_invoices || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No invoices yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.recent_invoices.map((inv, idx) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                        <TableCell>{inv.customer?.name ?? "—"}</TableCell>
                        <TableCell align="right">₹{parseFloat(inv.total_amount).toLocaleString("en-IN")}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: parseFloat(inv.total_due_amount) > 0 ? "#ff5252" : "#4caf50", fontWeight: 600 }}
                        >
                          ₹{parseFloat(inv.total_due_amount).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                            size="small"
                            sx={{
                              bgcolor: alpha(STATUS_COLORS[inv.status] || "#9e9e9e", 0.12),
                              color: STATUS_COLORS[inv.status] || "#9e9e9e",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>

        {/* Recent Leads */}
        <Grid size={{ xs: 12 }}>
          <ChartCard title="Recent Leads">
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Sr. No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Lift Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recent_leads || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No leads yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.recent_leads.map((lead, idx) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{lead.client_name}</TableCell>
                        <TableCell>{lead.customer?.name ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={lead.lift_type?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                            size="small"
                            sx={{
                              bgcolor: alpha(STATUS_COLORS[lead.status] || "#9e9e9e", 0.12),
                              color: STATUS_COLORS[lead.status] || "#9e9e9e",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>{new Date(lead.created_at).toLocaleDateString("en-IN")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
